import { Box, CircularProgress } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useMember } from "api/members";
import { ReactComponent as OpenLoyaltyLogo } from "components/Icons/openLoyaltyIcon.svg";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { ADDITIONAL_METRICS_ID } from "state/tables/types";
import { Interval } from "types/members";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    loaderContainer: {
      width: "100%",
      height: "100%",
      minHeight: "260px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(12),
    },
  }),
);

export function AdditionalMetrics() {
  const classes = useStyles();
  const { useMemberAnalytics } = useMember();
  const { data: membersTotal } = useMemberAnalytics();
  const { data: membersWithoutTransaction } = useMemberAnalytics(false);
  const { data: membersWithTransaction } = useMemberAnalytics(true);
  const { message } = useMessage();
  const { setColumnOrdering } = useTablesStateHelpers();

  function createItems(items: Interval[]): Row[] {
    return items.map((item) => ({
      cells: [
        { content: message({ id: item.label }), property: "metric" },
        {
          align: "right",
          content: item.in_1_days,
          emptyString: "0",
          property: "lastDay",
        },
        {
          align: "right",
          content: item.in_7_days,
          emptyString: "0",
          property: "lastWeek",
        },
        {
          align: "right",
          content: item.in_30_days,
          emptyString: "0",
          property: "lastMonth",
        },
        {
          align: "right",
          content: item.in_365_days,
          emptyString: "0",
          property: "lastYear",
        },
      ],
      id: item.label,
    }));
  }

  const headItems = createHeadItems([
    {
      headerText: message({ id: "metric" }),
      property: "metric",
      isSortable: false,
    },
    {
      align: "right",
      headerText: message({ id: "lastDay" }),
      property: "lastDay",
      isSortable: false,
    },
    {
      align: "right",
      headerText: message({ id: "lastWeek" }),
      property: "lastWeek",
      isSortable: false,
    },
    {
      align: "right",
      headerText: message({ id: "lastMonth" }),
      property: "lastMonth",
      isSortable: false,
    },
    {
      align: "right",
      headerText: message({ id: "lastYear" }),
      property: "lastYear",
      isSortable: false,
    },
  ]);

  const createData = (
    membersTotal: Interval,
    membersWithoutTransaction: Interval,
    membersWithTransaction: Interval,
  ) => {
    return [
      { ...membersTotal, label: "membersTotal" },
      { ...membersWithoutTransaction, label: "membersWithoutTransaction" },
      { ...membersWithTransaction, label: "membersWithTransaction" },
    ];
  };
  const renderItems =
    membersTotal && membersWithoutTransaction && membersWithTransaction;
  const items = renderItems
    ? createItems(
        createData(
          membersTotal.intervals,
          membersWithoutTransaction.intervals,
          membersWithTransaction.intervals,
        ),
      )
    : [];

  return (
    <>
      {renderItems ? (
        <OLTable
          columns={headItems}
          disableActions
          disableColumnManager
          onSort={() => {}}
          rows={
            membersTotal &&
            membersWithoutTransaction &&
            membersWithTransaction &&
            items
          }
          setColumnOrdering={setColumnOrdering}
          sort={{ order: "asc", property: "", rowsPerPage: 10, page: 1 }}
          tableName={ADDITIONAL_METRICS_ID}
        />
      ) : (
        <Box className={classes.loaderContainer}>
          <OpenLoyaltyLogo />
          <CircularProgress />
        </Box>
      )}
    </>
  );
}
